<!--赖传峰-->
<!--2022/3/31-->
<script
  lang="ts"
  setup
>
import {
  computed,
  defineAsyncComponent,
  inject,
  onMounted,
  ref,
  useI18n,
} from '#imports'
import { blockSharedFiles } from '@/api'
import { isFolder } from '@/components/share/share.script'
import useMessage from '@/composables/useMessage'
import useParentId from '@/composables/useParentId'
import { useShare } from '@/store/share'
import {
  InjectionKey,
  ShareStatus,
} from '@/types/enum'
import { isShowBlockButton } from '@/utils'
import { ElMessageBox } from 'element-plus'
import useShareFiles from '~/composables/useShareFiles'

const AsyncReportSharedDialog = defineAsyncComponent(() => import('~/components/share/ReportSharedDialog.vue'))

const { showCoverAboveViewport = false } = defineProps<{ showCoverAboveViewport: boolean }>()

const reportDialogVisible = ref(false)
const showBlockButton = ref(false)
const blockLoading = ref(false)

const { t } = useI18n()
const shareStore = useShare()
const share = inject<ShareRef>(InjectionKey.SHARE)
const parentId = useParentId()
const files = useShareFiles()

const selectedLength = computed<number>(() => shareStore.selectedIds.length)

const showReportDialog = () => reportDialogVisible.value = true

const getFileById = (id: string): PPFile | null => {
  let file = null

  files.value.forEach(_file => {
    if (_file.id === id) {
      file = _file
    }
  })

  return file
}

const handleBlockByGcid = async () => {
  const gcids: string[] = []
  const fileIds: string[] = []

  ElMessageBox.confirm('Are you sure to block these files?', 'Block Files', {
    cancelButtonText: 'Cancel',
    confirmButtonText: 'Confirm',
  }).then(async () => {
    if (shareStore.selectedIds.length === 0) {
      // 在文件夹中, 直接屏蔽这个文件夹
      if (parentId.value) {
        fileIds.push(parentId.value)
      } else {
        files.value.forEach(file => {
          if (isFolder(file.kind)) {
            fileIds.push(file.id)
          } else {
            gcids.push(file.hash)
          }
        })
      }
    } else {
      shareStore.selectedIds.forEach(id => {
        const file = getFileById(id)
        if (!file) return
        if (isFolder(file.kind)) {
          fileIds.push(id)
        } else {
          gcids.push(file.hash)
        }
      })
    }
    const shareUserId = shareStore.data.user_info?.user_id || ''
    blockLoading.value = true
    const { error } = await blockSharedFiles({
      share_user_id: shareUserId,
      gcids,
      file_ids: fileIds,
    })
    blockLoading.value = false
    if (error.value && error.value.error_description) {
      useMessage({
        type: 'error',
        message: error.value.error_description,
      })
    } else {
      useMessage({
        type: 'success',
        message: 'Block success',
      })
    }
  }).catch(() => {})
}

onMounted(() => {
  showBlockButton.value = isShowBlockButton()
})

const leftTimeInfo = computed(() => {
  const leftDays = BigInt(share?.value?.expiration_left ?? 0)
  const leftSeconds = BigInt(share?.value?.expiration_left_seconds ?? 0)
  if (leftDays < 0) return t('share-not-expired')

  if (leftDays > 0) return t('share-expired-count', [leftDays])
  if (leftSeconds > 0) return t('share-expired-count', [1])
  return t('share-expired-title')
})

</script>

<template>
  <div
    v-if="share"
    :class="share.share_status === ShareStatus.OK && 'ok-status'"
    class="share-author"
  >
    <div style="flex: 1;">
      <div class="avatar">
        <ElImage :src="share.user_info?.avatar">
          <template #placeholder>
            <div class="avatar-placeholder" />
          </template>
          <template #error>
            <div class="avatar-placeholder" />
          </template>
        </ElImage>
      </div>
      <div class="description">
        <div class="author ellipsis">
          {{ $t('share-author', [share.user_info?.nickname]) }}
        </div>
        <div
          v-if="share.share_status === ShareStatus.OK"
          class="file-info"
        >
          <div>{{ leftTimeInfo }}</div>
        </div>
      </div>
    </div>
    <ElButton
      v-if="showBlockButton"
      :loading="blockLoading"
      :style="{ 'margin-right': '16px' }"
      type="primary"
      @mousedown.stop.prevent
      @click.stop.prevent="handleBlockByGcid"
    >
      Block ({{ selectedLength ? selectedLength : 'All' }})
    </ElButton>
    <template v-if="share.share_status === ShareStatus.OK && !showCoverAboveViewport">
      <a
        id="report-shared"
        class="report pp-link-button"
        @click="showReportDialog"
      >
        <span>{{ $t('report') }}</span>
      </a>
      <AsyncReportSharedDialog
        v-if="reportDialogVisible"
        v-model:visible="reportDialogVisible"
      />
    </template>
  </div>
</template>

<style lang="less">
@import '@/assets/styles/util.less';

.share-author {
  --avatar-size: 72px;

  display: flex;
  align-items: center;
  flex: 1;

  > div {
    align-items: center;
    display: flex;
    flex-direction: column;
  }

  .avatar {
    width: var(--avatar-size);
    height: var(--avatar-size);
    flex: 0 0 auto;
    border-radius: 50%;
    overflow: hidden;
    background: #d5eefc;
  }

  .author {
    font-weight: 700;
    margin-top: 12px;
    font-size: 15px;
    line-height: 18px;
  }

  .avatar-placeholder {
    width: var(--avatar-size);
    height: var(--avatar-size);
    background: url('@/assets/images/share/share-author-avatar.png');
    background-size: 100%;
  }

  &.ok-status {
    --avatar-size: 42px;
    flex-direction: row;
    height: 72px;
    padding: 0 16px;

    > div {
      display: flex;
      align-items: center;
      flex-direction: row;
    }

    .author {
      margin-top: 0;
      font-weight: 600;
      font-size: 15px;
      line-height: 22px;
    }

    .description {
      margin-left: 16px;
      min-width: 0;
      display: flex;
      flex-direction: column;
      justify-content: center;
      gap: 2px;
    }

    .file-info {
      font-size: 13px;
      color: var(--color-tertiary-text);
      display: flex;

      span {
        margin: 0 8px;

        &.divider {
          color: fade(@color-tertiary-text, 30%);
        }
      }
    }
  }

  .report {
    span {
      color: var(--color-secondary-text);
    }
  }

  @media screen and (min-width: @mobile-break-width) {
    .report {
      line-height: 36px;
      padding: 0 24px;
      border: 1px solid var(--color-primary);
      border-radius: 4px;

      span {
        color: var(--color-primary);
      }

      &:hover {
        border-color: var(--color-primary-hover);

        span {
          color: var(--color-primary-hover);
        }
      }
    }
  }
}

</style>
